import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Mail, Linkedin, Instagram, FileText, MessageCircle } from 'lucide-react';
import { CHATGPT_URL } from '../lib/chatgpt';
import { track } from '../lib/analytics';

// Inline X (Twitter) icon — lucide does not currently ship the new X glyph
const XIcon = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const CONTACT_EMAIL = 'info@fruchet.com';

const TermsContent = () => (
  <div className="space-y-4 text-sm leading-relaxed" style={{ color: 'hsl(var(--gb-text))' }}>
    <p className="text-xs uppercase tracking-wider" style={{ color: 'hsl(var(--gb-text-muted))' }}>
      Last updated: {new Date().getFullYear()}
    </p>

    {/* Key Disclaimers — surfaced at the top */}
    <div
      className="rounded-md border p-4 space-y-4"
      style={{
        borderColor: 'hsl(var(--gb-gold))',
        backgroundColor: 'hsl(var(--gb-surface-2))',
      }}
    >
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>
          Not Financial Advice
        </h4>
        <p>
          GeoBull / GeoBear is a financial data analytics and informational platform.{' '}
          <strong>GeoBull / GeoBear is not a registered investment adviser, broker-dealer,
          financial institution, or fiduciary.</strong>{' '}
          Nothing on our platform constitutes investment advice, financial advice, trading advice,
          or any recommendation to buy, sell, or hold any security or financial product. You should
          not make any investment decision based solely on content generated by the platform.
        </p>
      </div>
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>
          AI Outputs Can Be Wrong
        </h4>
        <p>
          GeoBulls / GeoBears uses artificial intelligence and machine learning. AI-generated outputs may contain errors,
          inaccuracies, or hallucinations. AI models are probabilistic, trained on historical data, and may produce results
          that appear plausible but are factually incorrect. AI outputs are not investment recommendations and should never
          be the sole basis for any financial decision.
        </p>
      </div>
      <div>
        <h4 className="font-semibold text-sm uppercase tracking-wider mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>
          Limitation of Liability
        </h4>
        <p>
          Your use of GeoBull / GeoBear is entirely at your own risk.{' '}
          <strong>You are solely responsible for your own investment decisions</strong>{' '}
          and any consequences arising from them. We disclaim all liability for losses, damages, or
          costs resulting from reliance on platform content.
        </p>
      </div>
    </div>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>1. Acceptance of Terms</h3>
      <p>
        By accessing or using GeoBull / GeoBear (the "Service"), operated by Fruchet Consulting LLC ("Company", "we", "us"),
        you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>2. Informational Use Only — Not Investment Advice</h3>
      <p>
        The Service generates analytical content about publicly traded companies and geopolitical events using artificial
        intelligence and third-party data sources. All content is provided strictly for informational and educational purposes.
        Nothing on the Service constitutes investment, legal, tax, accounting, or other professional advice, nor an offer or
        solicitation to buy or sell any security. You are solely responsible for your investment decisions and should consult
        a licensed professional before acting on any information.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>3. AI-Generated Output</h3>
      <p>
        Output is produced by large language models in combination with third-party data feeds (including but not limited to
        SEC filings, market-data providers, and web search providers). AI output is probabilistic and may contain errors, omissions,
        outdated facts, or fabricated references ("hallucinations"). You must independently verify any factual claim before
        relying on it. The Service makes no warranty as to accuracy, completeness, timeliness, or fitness for any purpose.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>4. No Fiduciary Relationship</h3>
      <p>
        Use of the Service does not create any advisory, fiduciary, broker-dealer, investment advisor, or client relationship
        between you and Fruchet Consulting LLC. We are not registered as an investment adviser, broker-dealer, or financial
        planner with any regulator.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>5. Acceptable Use</h3>
      <p>
        You agree not to: (a) reverse engineer, scrape, or attempt to extract underlying prompts or model weights;
        (b) use the Service to defame, harass, or mislead third parties; (c) submit content that violates any law or
        third-party right; (d) use the Service to make automated trading decisions without independent human review.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>6. Intellectual Property</h3>
      <p>
        The Service, including its design, prompts, branding, and software, is owned by Fruchet Consulting LLC and protected
        by intellectual property laws. Generated reports may be used for your internal, non-commercial reference. Republication,
        resale, or redistribution requires prior written consent.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>7. Disclaimer of Warranties</h3>
      <p>
        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING
        WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
        SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>8. Limitation of Liability</h3>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, FRUCHET CONSULTING LLC AND ITS AFFILIATES, OFFICERS, EMPLOYEES, AND AGENTS
        SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS,
        REVENUE, DATA, OR INVESTMENT LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE, WHETHER BASED ON CONTRACT,
        TORT, OR ANY OTHER LEGAL THEORY.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>9. Indemnification</h3>
      <p>
        You agree to indemnify and hold harmless Fruchet Consulting LLC from any claim, loss, or expense arising out of your
        use of the Service, your violation of these Terms, or your violation of any third-party right.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>10. Governing Law</h3>
      <p>
        These Terms are governed by the laws of the State of Delaware, United States, without regard to its conflict-of-laws
        principles. Exclusive jurisdiction lies with the state and federal courts located in Delaware.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>11. Changes</h3>
      <p>
        We may modify these Terms at any time by posting an updated version. Continued use after changes constitutes acceptance.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>12. Contact</h3>
      <p>
        Questions: <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: 'hsl(var(--gb-navy))' }}>{CONTACT_EMAIL}</a>
      </p>
    </section>
  </div>
);

const PrivacyContent = () => (
  <div className="space-y-4 text-sm leading-relaxed" style={{ color: 'hsl(var(--gb-text))' }}>
    <p className="text-xs uppercase tracking-wider" style={{ color: 'hsl(var(--gb-text-muted))' }}>
      Last updated: {new Date().getFullYear()}
    </p>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>1. Who We Are</h3>
      <p>
        GeoBull / GeoBear is operated by Fruchet Consulting LLC ("we", "us"). This Privacy Policy describes how we collect,
        use, and safeguard information when you use the Service.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>2. Information We Collect</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li><strong>Inputs:</strong> the company tickers and event descriptions you submit for analysis.</li>
        <li><strong>Technical data:</strong> IP address, browser, device type, and standard server logs.</li>
        <li><strong>Communications:</strong> any email or message you voluntarily send us.</li>
      </ul>
      <p className="mt-2">
        We do <strong>not</strong> require you to create an account and we do <strong>not</strong> request payment information.
        We do not knowingly collect information from children under 13.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>3. How We Use Information</h3>
      <ul className="list-disc pl-5 space-y-1">
        <li>To generate the requested analysis.</li>
        <li>To monitor reliability, prevent abuse, and improve the Service.</li>
        <li>To respond to inquiries you send us.</li>
        <li>To comply with legal obligations.</li>
      </ul>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>4. Third-Party Processors</h3>
      <p>
        To deliver the Service, your inputs and limited derived context may be transmitted to third-party providers,
        including AI/LLM inference providers, web search providers, financial reference data providers, and publicly
        available regulatory filing sources. Each provider is governed by its own privacy policy. We do not sell your data.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>5. Cookies & Analytics</h3>
      <p>
        The Service uses only essential storage required for the application to function. We do not currently run third-party
        advertising trackers. If this changes, we will update this policy and, where required, request your consent.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>6. Data Retention</h3>
      <p>
        Submitted inputs are processed in real time. Server logs are retained only for the period necessary to operate, secure,
        and improve the Service, after which they are deleted or anonymized.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>7. Security</h3>
      <p>
        We use industry-standard technical and organizational measures, including TLS encryption in transit. No method of
        transmission or storage is 100% secure; we cannot guarantee absolute security.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>8. Your Rights</h3>
      <p>
        Depending on your jurisdiction (e.g., GDPR, CCPA), you may have the right to access, correct, delete, or restrict the
        processing of your personal data, and to lodge a complaint with a supervisory authority. To exercise these rights,
        contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: 'hsl(var(--gb-navy))' }}>{CONTACT_EMAIL}</a>.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>9. International Transfers</h3>
      <p>
        Information may be processed in jurisdictions other than your own, including the United States. By using the Service,
        you consent to such transfers, subject to applicable law.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>10. Changes to This Policy</h3>
      <p>
        We may update this Privacy Policy from time to time. The "last updated" date above reflects the most recent revision.
      </p>
    </section>

    <section>
      <h3 className="font-semibold text-base mb-1" style={{ color: 'hsl(var(--gb-navy))' }}>11. Contact</h3>
      <p>
        Privacy inquiries: <a href={`mailto:${CONTACT_EMAIL}`} className="underline" style={{ color: 'hsl(var(--gb-navy))' }}>{CONTACT_EMAIL}</a>
      </p>
    </section>
  </div>
);

const SocialLink = ({ href, label, disabled, children, testId }) => {
  const baseStyle = {
    color: disabled ? 'hsl(var(--gb-midgrey))' : 'hsl(var(--gb-navy))',
    borderColor: 'hsl(var(--gb-border))',
    backgroundColor: 'hsl(var(--gb-surface))',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.55 : 1,
  };

  if (disabled) {
    return (
      <span
        data-testid={testId}
        aria-label={`${label} (coming soon)`}
        title={`${label} — coming soon`}
        className="inline-flex items-center justify-center h-9 w-9 rounded-full border transition-colors"
        style={baseStyle}
      >
        {children}
      </span>
    );
  }

  return (
    <a
      data-testid={testId}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="inline-flex items-center justify-center h-9 w-9 rounded-full border transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2"
      style={baseStyle}
    >
      {children}
    </a>
  );
};

const FooterLinks = () => {
  const [termsOpen, setTermsOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const linkBtnClass =
    'underline-offset-4 hover:underline focus-visible:underline focus-visible:outline-none transition-colors';
  const linkBtnStyle = { color: 'hsl(var(--gb-navy))' };

  return (
    <>
      <div
        data-testid="footer-links"
        className="flex flex-col md:flex-row md:items-center md:justify-between gap-4"
      >
        {/* Left: © + Legal + Contact + Demo */}
        <nav
          aria-label="Footer navigation"
          className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs"
        >
          <span data-testid="footer-copyright" style={{ color: 'hsl(var(--gb-gold))' }}>
            © Fruchet Consulting LLC
          </span>
          <span aria-hidden="true" style={{ color: 'hsl(var(--gb-midgrey))' }}>·</span>
          <button
            type="button"
            data-testid="footer-terms-btn"
            onClick={() => setTermsOpen(true)}
            className={linkBtnClass}
            style={linkBtnStyle}
          >
            Terms of Service
          </button>
          <span aria-hidden="true" style={{ color: 'hsl(var(--gb-midgrey))' }}>·</span>
          <button
            type="button"
            data-testid="footer-privacy-btn"
            onClick={() => setPrivacyOpen(true)}
            className={linkBtnClass}
            style={linkBtnStyle}
          >
            Privacy Policy
          </button>
          <span aria-hidden="true" style={{ color: 'hsl(var(--gb-midgrey))' }}>·</span>
          <a
            data-testid="footer-contact-link"
            href={`mailto:${CONTACT_EMAIL}?subject=GeoBull%20%2F%20GeoBear%20Inquiry`}
            className={`${linkBtnClass} inline-flex items-center gap-1.5`}
            style={linkBtnStyle}
          >
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            Contact
          </a>
          <span aria-hidden="true" style={{ color: 'hsl(var(--gb-midgrey))' }}>·</span>
          <a
            data-testid="footer-chatgpt-link"
            href={CHATGPT_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('footer_chatgpt_click', {})}
            className={`${linkBtnClass} inline-flex items-center gap-1.5`}
            style={linkBtnStyle}
            title="Opens a pre-filled question about GeoBull / GeoBear in ChatGPT"
          >
            <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
            Ask ChatGPT about GeoBull / GeoBear
          </a>
        </nav>

        {/* Right: Social icons */}
        <div className="flex items-center gap-2" aria-label="Social media">
          <SocialLink
            testId="footer-social-x"
            href="https://x.com/geobullbear"
            label="GeoBull / GeoBear on X"
          >
            <XIcon className="h-3.5 w-3.5" />
          </SocialLink>
          <SocialLink
            testId="footer-social-linkedin"
            href="https://www.linkedin.com/in/patrickfruchet"
            label="LinkedIn"
          >
            <Linkedin className="h-4 w-4" aria-hidden="true" />
          </SocialLink>
          <SocialLink
            testId="footer-social-instagram"
            label="Instagram"
            disabled
          >
            <Instagram className="h-4 w-4" aria-hidden="true" />
          </SocialLink>
        </div>
      </div>

      {/* Terms Modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent
          data-testid="terms-modal"
          className="max-w-2xl"
          style={{ backgroundColor: 'hsl(var(--gb-surface))' }}
        >
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: 'var(--font-display)',
                color: 'hsl(var(--gb-navy))',
              }}
              className="text-2xl"
            >
              Terms of Service
            </DialogTitle>
            <DialogDescription style={{ color: 'hsl(var(--gb-text-muted))' }}>
              Please read these terms carefully before using GeoBull / GeoBear.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <TermsContent />
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Privacy Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent
          data-testid="privacy-modal"
          className="max-w-2xl"
          style={{ backgroundColor: 'hsl(var(--gb-surface))' }}
        >
          <DialogHeader>
            <DialogTitle
              style={{
                fontFamily: 'var(--font-display)',
                color: 'hsl(var(--gb-navy))',
              }}
              className="text-2xl"
            >
              Privacy Policy
            </DialogTitle>
            <DialogDescription style={{ color: 'hsl(var(--gb-text-muted))' }}>
              How we handle the information you provide to GeoBull / GeoBear.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <PrivacyContent />
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FooterLinks;
